import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Link } from 'react-router-dom';

import { getMemberNotifications, markMemberNotificationsOpened } from '../../../util/api';
import MemberIcon from '../MemberIcon';

import css from './NotificationBell.module.css';

/**
 * The notifications bell (member area N4; Member Home design). The number of
 * new notifications comes with the inbox counts every page already loads; the
 * list is only fetched when the bell is opened.
 *
 * "New" = since the member last opened the bell (decision D11; the ACs said
 * "since their most recent log in", but logins are not recorded). Opening the
 * bell marks everything shown as no longer new.
 */

const TYPE_ICON = {
  enquiry: 'enq',
  introduction: 'intro',
  message: 'msg',
  kyc: 'kycOk',
  listing: 'list',
};

/** "12m", "3h", "Yesterday", "Mon", "12 Oct". Pure. */
export const shortTime = (iso, now = new Date()) => {
  const at = new Date(iso);
  const minutes = Math.floor((now - at) / 60000);
  if (minutes < 1) return 'Now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24 && at.getDate() === now.getDate()) return `${hours}h`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (at.toDateString() === yesterday.toDateString()) return 'Yesterday';
  if (now - at < 6 * 24 * 60 * 60 * 1000) {
    return at.toLocaleDateString('en-GB', { weekday: 'short' });
  }
  return at.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const NotificationBell = props => {
  const { count, onOpened, className, panelClassName } = props;
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [error, setError] = useState(false);
  const rootRef = useRef(null);

  // Any click outside closes it.
  useEffect(() => {
    if (!open) return undefined;
    const close = e => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (!next) return;
    setError(false);
    getMemberNotifications()
      .then(response => {
        setItems(response?.items || []);
        // Seen now: the next visit counts from here.
        return markMemberNotificationsOpened().then(() => onOpened && onOpened());
      })
      .catch(() => setError(true));
  };

  const newCount = (items || []).filter(i => i.isNew).length;

  return (
    <div className={classNames(css.root, className)} ref={rootRef}>
      <button
        type="button"
        className={classNames(css.bellButton, { [css.bellButtonOpen]: open })}
        onClick={toggle}
        aria-label={count > 0 ? `Notifications, ${count} new` : 'Notifications'}
        aria-expanded={open}
      >
        <MemberIcon name="bell" size={20} />
        {count > 0 ? <span className={css.badge}>{count > 99 ? '99+' : count}</span> : null}
      </button>

      {open ? (
        <div className={classNames(css.panel, panelClassName)}>
          <div className={css.panelHead}>
            <span className={css.panelTitle}>Notifications</span>
            {items && items.length ? (
              <span className={css.panelMeta}>
                {newCount ? `${newCount} new · ` : ''}
                {items.length === 20 ? '20 most recent' : `${items.length} in total`}
              </span>
            ) : null}
          </div>
          <div className={css.list}>
            {error ? <p className={css.empty}>The notifications could not be loaded.</p> : null}
            {!error && items === null ? <p className={css.empty}>Loading…</p> : null}
            {!error && items && items.length === 0 ? (
              <p className={css.empty}>No notifications yet.</p>
            ) : null}
            {(items || []).map((item, i) => {
              const showHead = i === 0 || (items[i - 1].isNew && !item.isNew);
              const body = (
                <>
                  <span className={css.itemIcon}>
                    <MemberIcon name={TYPE_ICON[item.type] || 'bell'} size={15} />
                  </span>
                  <span className={css.itemText}>
                    <span className={classNames(css.itemTitle, { [css.itemTitleNew]: item.isNew })}>
                      {item.title}
                    </span>
                    {item.sub ? <span className={css.itemSub}>{item.sub}</span> : null}
                  </span>
                  <span className={css.itemTime}>{shortTime(item.createdAt)}</span>
                  {item.isNew ? <span className={css.newDot} /> : null}
                </>
              );
              return (
                <React.Fragment key={item.id}>
                  {showHead ? (
                    <div className={css.groupHead}>{item.isNew ? 'New' : 'Earlier'}</div>
                  ) : null}
                  {item.link ? (
                    <Link
                      to={item.link}
                      className={classNames(css.item, { [css.itemNew]: item.isNew })}
                      onClick={() => setOpen(false)}
                    >
                      {body}
                    </Link>
                  ) : (
                    <div className={classNames(css.item, { [css.itemNew]: item.isNew })}>{body}</div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default NotificationBell;
